import { c as _c } from "react/compiler-runtime";
import { forwardRef, useCallback, useRef, useState } from 'react';
import { Button, Input, Text } from '@chakra-ui/react';
import { Checkbox } from '@/components/ui/checkbox';
import { DialogBody, DialogCloseTrigger, DialogContent, DialogFooter, DialogHeader, DialogRoot, DialogTitle } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { CopyKind } from './copyKind';
const WORKING_COPY_VALUE = 'working';
const RELEASE_BRANCH_VALUE = 'release';
export type InputModalProps = {
  isOpen: boolean;
  modalTitle: string;
  buttonText: string;
  onClose: () => void;
  onSubmit: (name: string, isReleaseBranch?: boolean, copyKind?: CopyKind) => void;
  defaultInputValue?: string;
  preventSavingDefaultValue?: boolean;
  /** Allow submitting an empty value (e.g. to clear a field). */
  allowEmpty?: boolean;
  /** KBE-882: when set, offers a "Create as delta branch" switch. A delta branch
   * copies no content and can't be a release branch, so while it is on the
   * working-copy/release-branch intent is suppressed. */
  deltaConfig?: {
    label: string;
  };
  /** KBE-981: when set (only for the master or a release branch), offers an
   * explicit "Create as ref branch" switch. It is never the default: a ref
   * branch re-bases onto the parent's head after a merge, so merging from it a
   * second time can revert changes others made to the parent meanwhile. */
  refBranchConfig?: {
    label: string;
    warning: string;
  };
  /** When set (only when duplicating the master), offers a "Create working
   * copy" / "Create release branch" choice. The release-branch name is
   * auto-computed (`releaseBranchName`, e.g. "3.24.X") and not user-editable.
   * See KBE-685. */
  intentConfig?: {
    releaseBranchName: string;
  };
};
const InputModal = forwardRef<HTMLDivElement, InputModalProps>((t0, ref) => {
  const $ = _c(64);
  const {
    isOpen,
    modalTitle,
    onClose,
    onSubmit,
    buttonText,
    defaultInputValue,
    preventSavingDefaultValue,
    allowEmpty,
    intentConfig,
    deltaConfig,
    refBranchConfig
  } = t0;
  const nameInputRef = useRef(null);
  const [name, setName] = useState(defaultInputValue ?? "");
  const [isReleaseBranch, setIsReleaseBranch] = useState(false);
  const [copyKind, setCopyKind] = useState("copy");
  const isNameInvalid = isReleaseBranch ? false : !name && !allowEmpty;
  let t1;
  if ($[0] !== defaultInputValue) {
    t1 = () => {
      setName(defaultInputValue ?? "");
      setIsReleaseBranch(false);
      setCopyKind("copy");
    };
    $[0] = defaultInputValue;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const resetState = t1;
  let t2;
  if ($[2] !== copyKind || $[3] !== defaultInputValue || $[4] !== isNameInvalid || $[5] !== isReleaseBranch || $[6] !== name || $[7] !== onClose || $[8] !== onSubmit || $[9] !== preventSavingDefaultValue || $[10] !== resetState) {
    t2 = () => {
      if (preventSavingDefaultValue && defaultInputValue === name) {
        return;
      }
      if (isNameInvalid) {
        return;
      }
      onSubmit(name, isReleaseBranch, copyKind);
      onClose();
      resetState();
    };
    $[2] = copyKind;
    $[3] = defaultInputValue;
    $[4] = isNameInvalid;
    $[5] = isReleaseBranch;
    $[6] = name;
    $[7] = onClose;
    $[8] = onSubmit;
    $[9] = preventSavingDefaultValue;
    $[10] = resetState;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const onSave = t2;
  let t3;
  if ($[12] !== defaultInputValue || $[13] !== isReleaseBranch) {
    t3 = (kind, checked) => {
      setCopyKind(checked ? kind : "copy");
      if (checked && isReleaseBranch) {
        setIsReleaseBranch(false);
        setName(defaultInputValue ?? "");
      }
    };
    $[12] = defaultInputValue;
    $[13] = isReleaseBranch;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const onCopyKindChange = t3;
  let t4;
  if ($[15] !== defaultInputValue || $[16] !== intentConfig?.releaseBranchName) {
    t4 = value => {
      const nextIsRelease = value === RELEASE_BRANCH_VALUE;
      setIsReleaseBranch(nextIsRelease);
      setName(nextIsRelease ? intentConfig?.releaseBranchName ?? "" : defaultInputValue ?? "");
    };
    $[15] = defaultInputValue;
    $[16] = intentConfig?.releaseBranchName;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  intentConfig?.releaseBranchName;
  const onIntentChange = t4;
  let t5;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => nameInputRef.current;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== onClose || $[20] !== resetState) {
    t6 = e => {
      if (!e.open) {
        onClose();
        resetState();
      }
    };
    $[19] = onClose;
    $[20] = resetState;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== modalTitle) {
    t7 = <DialogHeader><DialogTitle>{modalTitle}</DialogTitle></DialogHeader>;
    $[22] = modalTitle;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <DialogCloseTrigger />;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== copyKind || $[26] !== intentConfig || $[27] !== isReleaseBranch || $[28] !== onIntentChange) {
    t9 = intentConfig && copyKind === "copy" && <Field mb={3}><SegmentedControl items={[{
        value: WORKING_COPY_VALUE,
        label: "Create working copy"
      }, {
        value: RELEASE_BRANCH_VALUE,
        label: "Create release branch"
      }]} value={isReleaseBranch ? RELEASE_BRANCH_VALUE : WORKING_COPY_VALUE} onValueChange={e_0 => onIntentChange(e_0.value ?? WORKING_COPY_VALUE)} />{isReleaseBranch && <Text color="gray.500" fontSize="xs" mt={1}>Release-branch name is set automatically from the master and the latest release branch.</Text>}</Field>;
    $[25] = copyKind;
    $[26] = intentConfig;
    $[27] = isReleaseBranch;
    $[28] = onIntentChange;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = e_1 => setName(e_1.currentTarget.value);
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== isNameInvalid || $[32] !== onSave) {
    t11 = e_3 => {
      if (e_3.key === "Enter" && !isNameInvalid && e_3.currentTarget === document.activeElement) {
        onSave();
      }
    };
    $[31] = isNameInvalid;
    $[32] = onSave;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== isReleaseBranch || $[35] !== name || $[36] !== t11) {
    t12 = <Field><Input ref={nameInputRef} placeholder="Name" readOnly={isReleaseBranch} value={name} onChange={t10} onFocus={_temp} onKeyDown={t11} /></Field>;
    $[34] = isReleaseBranch;
    $[35] = name;
    $[36] = t11;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== copyKind || $[39] !== deltaConfig || $[40] !== onCopyKindChange) {
    t13 = deltaConfig && <Field mt={3}><Checkbox checked={copyKind === "delta"} onCheckedChange={e_4 => onCopyKindChange("delta", !!e_4.checked)}>{deltaConfig.label}</Checkbox></Field>;
    $[38] = copyKind;
    $[39] = deltaConfig;
    $[40] = onCopyKindChange;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== copyKind || $[43] !== onCopyKindChange || $[44] !== refBranchConfig) {
    t14 = refBranchConfig && <Field mt={3}><Checkbox checked={copyKind === "ref"} onCheckedChange={e_5 => onCopyKindChange("ref", !!e_5.checked)}>{refBranchConfig.label}</Checkbox>{copyKind === "ref" && <Text color="orange.600" fontSize="xs" mt={1}>{refBranchConfig.warning}</Text>}</Field>;
    $[42] = copyKind;
    $[43] = onCopyKindChange;
    $[44] = refBranchConfig;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  let t15;
  if ($[46] !== t12 || $[47] !== t13 || $[48] !== t14 || $[49] !== t9) {
    t15 = <DialogBody>{t9}{t12}{t13}{t14}</DialogBody>;
    $[46] = t12;
    $[47] = t13;
    $[48] = t14;
    $[49] = t9;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  const t16 = isNameInvalid || preventSavingDefaultValue && name === defaultInputValue;
  let t17;
  if ($[51] !== buttonText || $[52] !== onSave || $[53] !== t16) {
    t17 = <DialogFooter><Button disabled={t16} onClick={onSave}>{buttonText}</Button></DialogFooter>;
    $[51] = buttonText;
    $[52] = onSave;
    $[53] = t16;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  let t18;
  if ($[55] !== ref || $[56] !== t15 || $[57] !== t17 || $[58] !== t7) {
    t18 = <DialogContent ref={ref}>{t7}{t8}{t15}{t17}</DialogContent>;
    $[55] = ref;
    $[56] = t15;
    $[57] = t17;
    $[58] = t7;
    $[59] = t18;
  } else {
    t18 = $[59];
  }
  let t19;
  if ($[60] !== isOpen || $[61] !== t18 || $[62] !== t6) {
    t19 = <DialogRoot initialFocusEl={t5} open={isOpen} onOpenChange={t6}>{t18}</DialogRoot>;
    $[60] = isOpen;
    $[61] = t18;
    $[62] = t6;
    $[63] = t19;
  } else {
    t19 = $[63];
  }
  return t19;
});
InputModal.displayName = 'InputModal';
export default InputModal;
function _temp(e_2) {
  return e_2.currentTarget.select();
}